import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, X, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";

interface Profile {
  phone?: string | null;
  description?: string | null;
  communes_couvertes?: string[] | null;
  portfolio_count?: number | null;
  logo_url?: string | null;
  created_at?: string;
}

interface ProfileCompletionCardProps {
  profile: Profile;
}

const STORAGE_KEY = "profile_completion_dismissed";

const STEPS = [
  {
    key: "phone",
    label: "Numéro de téléphone",
    weight: 15,
    tab: "informations",
    check: (p: Profile) => !!p.phone,
  },
  {
    key: "description",
    label: "Description de votre activité",
    weight: 20,
    tab: "informations",
    check: (p: Profile) => !!p.description && p.description.trim().length > 0,
  },
  {
    key: "communes_couvertes",
    label: "Zones d'intervention",
    weight: 15,
    tab: "informations",
    check: (p: Profile) => Array.isArray(p.communes_couvertes) && p.communes_couvertes.length > 0,
  },
  {
    key: "portfolio",
    label: "Au moins une photo portfolio",
    weight: 30,
    tab: "portfolio",
    check: (p: Profile) => (p.portfolio_count ?? 0) > 0,
  },
  {
    key: "logo",
    label: "Photo de profil / logo",
    weight: 20,
    tab: "informations",
    check: (p: Profile) => !!p.logo_url,
  },
] as const;

const ProfileCompletionCard = ({ profile }: ProfileCompletionCardProps) => {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "true"
  );

  if (dismissed) return null;

  const score = STEPS.reduce((acc, step) => (step.check(profile) ? acc + step.weight : acc), 0);
  if (score === 100) return null;

  const firstIncomplete = STEPS.find((s) => !s.check(profile));

  const isNew =
    profile.created_at
      ? Date.now() - new Date(profile.created_at).getTime() < 72 * 60 * 60 * 1000
      : false;

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setDismissed(true);
  };

  const handleCTA = () => {
    if (firstIncomplete) {
      navigate(`/mon-profil-artisan?tab=${firstIncomplete.tab}`);
    }
  };

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Rocket className="h-4 w-4 text-primary shrink-0" aria-hidden="true" />
            <div>
              {isNew && (
                <p className="text-xs font-medium text-primary mb-0.5">Bienvenue sur Hirafi !</p>
              )}
              <p className="text-sm font-semibold leading-tight">
                Complétez votre profil — {score}%
              </p>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5"
            aria-label="Masquer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <Progress value={score} className="h-1.5 mb-4" />

        <ul className="space-y-1.5 mb-4">
          {STEPS.map((step) => {
            const done = step.check(profile);
            return (
              <li key={step.key} className="flex items-center gap-2 text-sm">
                {done ? (
                  <CheckCircle2 className="h-4 w-4 text-accent shrink-0" aria-hidden="true" />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                )}
                <span className={done ? "line-through text-muted-foreground" : ""}>{step.label}</span>
                {!done && (
                  <span className="ml-auto text-xs text-muted-foreground font-medium">+{step.weight}%</span>
                )}
              </li>
            );
          })}
        </ul>

        {firstIncomplete && (
          <Button variant="default" size="sm" className="w-full" onClick={handleCTA}>
            Compléter mon profil
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileCompletionCard;
